import React, { useEffect, useState } from "react";
import { APP_VERSION, APP_BUILD, APP_CODENAME } from "@/lib/version";
import { CheckCircle, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";

/**
 * Small version pill shown at the bottom of the Login page. Displays the
 * frontend build version this device is running AND compares it to the
 * backend's advertised version. When the two diverge (installed PWA is on
 * a stale bundle), a "Force refresh" button appears that hard-clears the
 * service worker + caches and reloads.
 *
 * User request v1.24.8 — "on the login page there should be some version
 * info so that can be check on that version i am working."
 */
export default function LoginVersionPill() {
    const [backend, setBackend] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let stop = false;
        const load = async () => {
            try {
                const res = await fetch("/api/version", { cache: "no-store" });
                if (!res.ok) return;
                const j = await res.json();
                if (!stop) setBackend(j);
            } catch { /* offline */ }
        };
        load();
        const id = setInterval(load, 60 * 1000);
        return () => { stop = true; clearInterval(id); };
    }, []);

    const forceRefresh = async () => {
        setBusy(true);
        try {
            if ("serviceWorker" in navigator) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
            }
            if (window.caches) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
            }
        } catch { /* non-fatal */ }
        window.location.replace(window.location.pathname + "?_v=" + Date.now());
    };

    const outOfDate = backend?.version && backend.version !== APP_VERSION;

    return (
        <div
            data-testid="login-version-pill"
            className={`mt-6 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-[11px] ${
                outOfDate
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
        >
            <div className="flex items-center gap-2">
                {outOfDate
                    ? <WarningCircle size={14} weight="fill" className="text-amber-700" />
                    : <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                }
                <div className="leading-tight">
                    <div className="font-semibold">
                        MICM <span data-testid="pill-app-version" className="font-mono">v{APP_VERSION}</span>
                        <span className="ml-1 text-slate-500">· build {APP_BUILD}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">
                        {outOfDate ? (
                            <>Server is on <span className="font-mono font-semibold">v{backend.version}</span> — your device is out of date.</>
                        ) : (
                            <>Server: <span className="font-mono">v{backend?.version || "…"}</span> · <span className="capitalize">{APP_CODENAME.replace(/-/g, " ")}</span></>
                        )}
                    </div>
                </div>
            </div>
            {outOfDate && (
                <button
                    type="button"
                    onClick={forceRefresh}
                    disabled={busy}
                    data-testid="pill-force-refresh"
                    className="inline-flex items-center gap-1 rounded-md bg-amber-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-amber-900 disabled:opacity-50"
                >
                    <ArrowClockwise size={11} weight="bold" className={busy ? "animate-spin" : ""} />
                    {busy ? "Refreshing…" : "Force refresh"}
                </button>
            )}
        </div>
    );
}
